import * as React from "react";

interface AnalyticsConsumerInterface<AnalyticsInterface extends unknown> {
  analytics: Required<AnalyticsInterface>;
  implementations: AnalyticsInterface[];
}

export const createAnalyticsContext = <AnalyticsInterface extends unknown>(): [
  React.Context<AnalyticsConsumerInterface<AnalyticsInterface>>,
  () => AnalyticsConsumerInterface<AnalyticsInterface>
] => {
  const defaultValue =
    {} as unknown as AnalyticsConsumerInterface<AnalyticsInterface>;

  const AnalyticsContext = React.createContext(defaultValue);

  const useAnalyticsProvider = () => {
    const context = React.useContext(AnalyticsContext);
    if (context === defaultValue) {
      throw new Error(
        "useAnalyticsProvider must be used within a AnalyticsProvider"
      );
    }
    return context;
  };

  return [AnalyticsContext, useAnalyticsProvider];
};

interface AnalyticsProviderProps<AnalyticsInterface extends unknown> {
  context: React.Context<AnalyticsConsumerInterface<AnalyticsInterface>>;

  implementations: AnalyticsInterface[];

  children?: React.ReactNode | undefined;
}

export const AnalyticsProvider = <AnalyticsInterface extends unknown>(
  props: AnalyticsProviderProps<AnalyticsInterface>
): React.ReactElement<any, any> | null => {
  const handle_any_analytics_event = (eventName: string, packet: any) => {
    props.implementations.forEach((implementation) => {
      (implementation as unknown as { [key: string]: (packet: any) => void })[
        eventName
      ]?.(packet);
    });
  };

  const analytics_pretend_object = {};
  const analytics_proxy_handler = {
    get: (target: any, name: any) => {
      return (packet: any) => {
        return handle_any_analytics_event(name, packet);
      };
    },
  };
  const analytics_proxy = new Proxy(
    analytics_pretend_object,
    analytics_proxy_handler
  ) as Required<AnalyticsInterface>;

  const value: AnalyticsConsumerInterface<AnalyticsInterface> = React.useMemo(
    () => ({
      analytics: analytics_proxy,
      implementations: props.implementations,
    }),
    [props, analytics_proxy]
  );

  return (
    <props.context.Provider value={value}>
      {props?.children || []}
    </props.context.Provider>
  );
};
